import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useAuthState } from "react-firebase-hooks/auth";
import auth from "../../firebase.init";

const ThankYouPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetch(`https://ecom-22-fe6311cae608.herokuapp.com/order/${id}`)
      .then((res) => res.json())
      .then((info) => {
        if (info && info.orderId) {
          setOrder(info);
        } else {
          setError("Order not found");
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error loading order:", err);
        setError("Failed to load order details");
        setLoading(false);
      });
  }, [id]);

  if (loading) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center min-vh-100 py-5">
        <div
          className="spinner-border text-primary"
          role="status"
          style={{ width: "3rem", height: "3rem" }}
        >
          <span className="visually-hidden"></span>
        </div>
        <p className="mt-3 fw-medium text-primary">
          Confirming your payment details...
        </p>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6">
            <div className="alert alert-warning shadow-sm border-0 rounded-3 p-4">
              <h4 className="alert-heading mb-3">
                <i className="bi bi-question-circle-fill me-2"></i>
                Attention
              </h4>
              <p className="mb-3">
                {error || "We could not verify your order status. If you completed payment, please check your subscription history."}
              </p>
              <button
                className="btn btn-primary px-4 py-2"
                onClick={() => navigate("/user/subscription-history")}
              >
                Check Subscription History
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Thank You | Payment Successful</title>
        <meta
          name="description"
          content="Your payment was successful. Thank you for your purchase."
        />
      </Helmet>
      <main>
        {/* Success Header Section */}
        <section
          className="position-relative py-5 bg-success bg-gradient text-white"
          style={{
            background: "linear-gradient(135deg, #28a745 0%, #208f38 100%)",
            overflow: "hidden",
          }}
        >
          <div className="container position-relative" style={{ zIndex: "1" }}>
            <div className="row justify-content-center text-center">
              <div className="col-lg-8">
                <div className="mb-4">
                  <i className="bi bi-check-circle-fill" style={{ fontSize: "4rem", color: "#fff" }}></i>
                </div>
                <h1 className="fw-bold mb-3">Payment Successful!</h1>
                <p className="lead mb-0 opacity-75">
                  Thank you for your purchase. Your order has been confirmed.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Order Details Section */}
        <section className="py-5 bg-light">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-10 col-xl-8">
                <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                  
                  {/* Summary Header */}
                  <div className="card-header bg-white p-4 border-bottom border-light">
                    <div className="d-flex justify-content-between align-items-center flex-wrap">
                      <div>
                        <h4 className="mb-1 fw-bold text-dark">
                          {order.packageName || "Product Package"}
                        </h4>
                        <p className="text-muted mb-0">
                          Order ID: <span className="fw-medium">{order.orderId}</span>
                        </p>
                      </div>
                      <div className="text-end mt-3 mt-md-0">
                        <span className="badge bg-success rounded-pill text-white px-3 py-2 fs-6">
                          {order.orderStatus || "Completed"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Summary Body */}
                  <div className="card-body p-4">
                    <div className="row g-4">
                      {/* Customer Info */}
                      <div className="col-md-6">
                        <div className="p-3 bg-light rounded-3 h-100">
                          <h5 className="mb-3 fw-semibold border-bottom pb-2 text-dark">
                            Customer Information
                          </h5>
                          <div className="mb-2 d-flex">
                            <span className="text-muted me-2" style={{ width: "100px" }}>Name:</span>
                            <span className="fw-medium">{order.customerName}</span>
                          </div>
                          <div className="mb-2 d-flex">
                            <span className="text-muted me-2" style={{ width: "100px" }}>Email:</span>
                            <span className="fw-medium">{order.customerEmail}</span>
                          </div>
                        </div>
                      </div>

                      {/* Transaction Info */}
                      <div className="col-md-6">
                        <div className="p-3 bg-light rounded-3 h-100">
                          <h5 className="mb-3 fw-semibold border-bottom pb-2 text-dark">
                            Transaction Details
                          </h5>
                          <div className="mb-2 d-flex justify-content-between">
                            <span className="text-muted">Amount Paid:</span>
                            <span className="fw-medium">${order.totalPrice} USD</span>
                          </div>
                           {order.credit && (
                            <div className="mb-2 d-flex justify-content-between">
                              <span className="text-muted">Credits Added:</span>
                              <span className="fw-medium">{order.credit} Credits</span>
                            </div>
                          )}
                          <div className="mb-2 d-flex justify-content-between">
                            <span className="text-muted">Payment Method:</span>
                            <span className="fw-medium">{order.paymentMethod || "PayPal"}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Call to Action Buttons */}
                    <div className="mt-5 pt-3 border-top text-center">
                      <h5 className="fw-bold mb-4">What would you like to do next?</h5>
                      <div className="d-flex flex-column flex-md-row justify-content-center gap-3">
                        <Link 
                          to="/user/subscription-history" 
                          className="btn btn-outline-primary px-4 py-2 rounded-pill"
                        >
                          <i className="bi bi-receipt me-2"></i>
                          View Subscription History
                        </Link>
                        <Link 
                          to="/user/dashboard" 
                          className="btn btn-primary px-4 py-2 rounded-pill shadow-sm"
                        >
                          <i className="bi bi-house me-2"></i>
                          Go to Dashboard
                        </Link>
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="card-footer bg-white p-4 text-center border-top border-light">
                    <p className="mb-0 small text-muted">
                      A confirmation email has been sent to <strong>{order.customerEmail}</strong>. 
                      If you have questions, please <Link to="/contact">contact support</Link>.
                    </p>
                  </div>

                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default ThankYouPage;